import React, { useEffect, useState, useRef, ReactElement, useMemo } from 'react';
// import ApproverCellDetailLine from './ApproverCellDetailLine';
import { GetApproverDetailsListGroups } from '../components/Utils';
import {
    Stack,
    Callout,
    getTheme,
    FontWeights,
    mergeStyleSets,
    Text,
    DetailsList,
    SelectionMode,
    IColumn,
    Icon,
    Spinner,
    IStackStyles,
    Label,
} from '@fluentui/react';
import { convertHoursToProcent, getCapacityPrDay } from '../components/WeekOverview/Cell.helpers';
import { ApproverFlaggedColumn } from './ApproverFlaggedColumn';
import { roundToNearestValueStep } from '../Mobile/mobileUtils';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../api/api';
import { ApproverCellDetailLine } from '../api/generated/data-contracts';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { useTranslation } from 'react-i18next';

const theme = getTheme();
const styles = mergeStyleSets({
    buttonArea: {
        verticalAlign: 'top',
        display: 'inline-block',
        textAlign: 'center',
        margin: '0 100px',
        minWidth: 130,
        height: 32,
    },
    callout: {
        maxWidth: 800,
        minWidth: 300,
    },
    header: {
        padding: '18px 24px 12px',
        color: theme.semanticColors.bodyText,
    },
    title: [
        theme.fonts.mediumPlus,
        {
            margin: 0,
            fontWeight: FontWeights.semilight,
        },
    ],
    inner: {
        height: '100%',
        padding: '0 24px 20px',
    },
    actions: {
        position: 'relative',
        marginTop: 20,
        width: '100%',
        whiteSpace: 'nowrap',
    },
    subtext: [
        theme.fonts.small,
        {
            margin: 0,
            fontWeight: FontWeights.semilight,
        },
    ],
    link: [
        theme.fonts.medium,
        {
            color: theme.palette.neutralPrimary,
        },
    ],
});
const loadingStackStyle: IStackStyles = { root: { height: '150px', width: '640px' } };
const spinnerStyles = {
    circle: {
        height: 25,
        width: 25,
    },
    label: {
        'font-size': 12,
    },
};

interface propsApproverCell {
    value: string;
    day: string;
    date?: Date;
    reportperiodid: string;
    userid: string;
    hasUserComments: boolean;
    disabled: boolean;
    periodDays?: any[];
}
function ApprovalCell(props: propsApproverCell) {
    const { t } = useTranslation('approval');
    const [showDetailsFlyout, setShowDetailsFlyout] = useState<boolean>(false);
    const [loadingDetails, setLoadingDetails] = useState<boolean>(true);

    const textdRef = useRef(null);

    const [detailitems, setDetailitems] = useState<ApproverCellDetailLine[]>([]);
    const [detailitemgroups, setSetailitemgroups] = useState<any[]>([]);

    const [svalue, setsValue] = useState<string>('');

    // const uiCtx = useUiContext();
    const {
        initialData: {
            configuration: { timeEntryRoundingInterval, gridInputType, gridView, allowCellFlag, capacityDefault },
            aggregatedUser: { user, delegatedUsers, originalUser },
        },
    } = useConfigValuesState();

    const userCapacity = useMemo(() => {
        const userWithId = [...delegatedUsers, originalUser, user].find(u => u.id === props.userid);
        return userWithId?.availability || userWithId?.calendar?.dailyCapacity || capacityDefault;
    }, [delegatedUsers, originalUser, user, props.userid, capacityDefault]);

    const columns = useMemo<IColumn[]>(
        () => [
            {
                key: 'name',
                name: t('approvalCell.table.project'),
                fieldName: 'taskName',
                minWidth: 200,
                maxWidth: 400,
                isResizable: true,
            },
            {
                key: 'hours',
                name: gridInputType === 'percent' ? '%' : t('approvalCell.table.hours'),
                fieldName: 'total',
                minWidth: 100,
                maxWidth: 200,
                isResizable: true,
            },
            ...(allowCellFlag
                ? [
                      {
                          key: 'flagged',
                          name: t('approvalCell.table.flag'),
                          fieldName: 'flagged',
                          minWidth: 100,
                          isResizable: true,
                      },
                  ]
                : []),
            {
                key: 'comments',
                name: t('approvalCell.table.comments'),
                fieldName: 'comments',
                minWidth: 200,
                maxWidth: 400,
                isResizable: true,
            },
        ],
        [t, gridInputType, allowCellFlag],
    );

    useEffect(() => {
        const value = Number(props.value);
        const newValue =
            gridInputType === 'percent' && props.periodDays
                ? convertHoursToProcent(value, props.periodDays.length, getCapacityPrDay(props.periodDays, userCapacity))
                : value;
        const roundedCellValue = roundToNearestValueStep(newValue, timeEntryRoundingInterval);
        setsValue(`${roundedCellValue}`);
    }, [props.value]);

    const onClick = (ev: React.MouseEvent<HTMLElement, MouseEvent>): void => {
        setShowDetailsFlyout(true);
        setLoadingDetails(true);
        const utcStart: any = props.date?.getISOStringMidnight();
        ApiCalls.getApproverLineByCustomPeriod({ userReportingPeriodId: props.reportperiodid, date: utcStart })
            .then(res => res.data)
            // dirty fix to get the comment from the first day of the period
            // it is used for the period view only
            .then(async json => {
                let detailLinesFirstDayOfPeriod: ApproverCellDetailLine[] | null;
                if (gridView === 'period') {
                    // const firstDayOfPeriod = await uiCtx.timeApi.getApprovalLinesByUserCustomPeriod(props.reportperiodid, props.periodDays[0].date.getISOStringMidnight());
                    const firstDayOfPeriod = await ApiCalls.getApproverLineByCustomPeriod({
                        userReportingPeriodId: props.reportperiodid,
                        date: props.periodDays[0].date.getISOStringMidnight(),
                    }).then(res => res.data);
                    detailLinesFirstDayOfPeriod = firstDayOfPeriod;
                }
                return { json, detailLinesFirstDayOfPeriod };
            })
            .then(({ json, detailLinesFirstDayOfPeriod }) => {
                const detaillines: ApproverCellDetailLine[] = [];
                json.map((detailline, index: number) => {
                    if (detailLinesFirstDayOfPeriod) {
                        const currentLine = detailLinesFirstDayOfPeriod.find((line: any) => line.taskId === detailline.taskId);
                        detailline.comments = currentLine?.comments;
                    }
                    // fix end
                    detaillines.push(
                        detailline,
                        // new ApproverCellDetailLine(detailline.projectId, detailline.projectName, detailline.taskId, detailline.taskName, detailline.total, detailline.comments, detailline.flagged),
                    );
                });
                const sortedlines: ApproverCellDetailLine[] = detaillines;
                setDetailitems(sortedlines);
                if (sortedlines.length > 0) {
                    let grouped: any = [];
                    grouped = GetApproverDetailsListGroups(sortedlines);
                    setSetailitemgroups(grouped);
                } else {
                    setSetailitemgroups([]);
                }
                setShowDetailsFlyout(true);
                setLoadingDetails(false);
            });
    };
    const onRenderColumn = (item: ApproverCellDetailLine, index: number, column: IColumn): ReactElement => {
        const fieldContent = item[column.fieldName as keyof ApproverCellDetailLine] as string;
        switch (column.key) {
            case 'hours':
                const value =
                    gridInputType === 'percent' && props.periodDays
                        ? convertHoursToProcent(Number(fieldContent), props.periodDays.length, getCapacityPrDay(props.periodDays, userCapacity))
                        : Number(fieldContent);
                const roundedCellValue = roundToNearestValueStep(value, timeEntryRoundingInterval);
                return <span>{roundedCellValue}</span>;
            // return <span>{uiCtx.gridInputType === "percent" ? `${MathToFixed(parseNumber(value.toString().replace(/\,/g, '.')), 1)}` : Format2DecimalValue(`${value}`)}</span>;
            case 'flagged':
                return <ApproverFlaggedColumn flagged={item.flagged} alignLeft />;
            default:
                return <span>{fieldContent}</span>;
        }
    };
    const flyoutDismissed = (ev: any): void => {
        setShowDetailsFlyout(false);
    };
    return (
        <>
            <Label>
                <span
                    ref={textdRef}
                    className={!props.disabled ? 'approvalcelltextspan' : 'approvalcelltextspandisabled'}
                    onClick={!props.disabled ? onClick : () => {}}
                    style={{ textAlign: 'center', display: 'block' }}
                >
                    {gridInputType === 'percent' ? `${svalue} %` : svalue}{' '}
                    {props.hasUserComments && (
                        <Icon
                            iconName="Comment"
                            styles={{ root: { fontSize: '12px', height: '10px', width: '14px', marginTop: '-4px', position: 'absolute' } }}
                        />
                    )}
                </span>
            </Label>
            {showDetailsFlyout && !props.disabled && (
                <Callout
                    className={styles.callout}
                    ariaLabelledBy="{labelId}"
                    ariaDescribedBy="{descriptionId}"
                    role="alertdialog"
                    gapSpace={0}
                    target={textdRef}
                    onDismiss={flyoutDismissed}
                    setInitialFocus
                >
                    <div className={styles.header}>
                        <Text className={styles.title}>{t('appovalCell.callout.title')}</Text>
                    </div>
                    {loadingDetails ? (
                        <Stack verticalAlign="center" horizontalAlign="center" styles={loadingStackStyle}>
                            <Spinner styles={spinnerStyles} label={t('approvalCell.loading')} />
                        </Stack>
                    ) : (
                        <div className={styles.inner}>
                            <DetailsList
                                //componentRef={this._root}
                                items={detailitems}
                                groups={detailitemgroups}
                                onRenderItemColumn={onRenderColumn}
                                columns={columns}
                                selectionMode={SelectionMode.none}
                                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                                ariaLabelForSelectionColumn="Toggle selection"
                                checkButtonAriaLabel="Row checkbox"
                                groupProps={{
                                    showEmptyGroups: true,
                                }}
                                compact={true}
                            />
                        </div>
                    )}
                </Callout>
            )}
        </>
    );
}

export default ApprovalCell;
